import { Field, Form, useFormikContext } from 'formik'
import React from 'react'

import { Checkbox, Fieldset } from '@fourthwall/components'
import type { PlatformValue, SearchChannel } from '@fourthwall/module-create-site'
import {
  ChannelAutoComplete,
  ChannelInput,
  CreateSiteModal,
  CreatorNameInput,
  LocationSelect,
  PlatformSelect,
  SiteNameInput,
  isPlatformSearchable,
} from '@fourthwall/module-create-site'
import { useFocusFirstErrorOnSubmit } from '@fourthwall/utils/dist/formik'

import type { FormValues } from './CreateNewSiteModal.types'
import * as S from './CreateNewSiteModalView.styled'

export type CreateNewSiteModalViewProps = {
  searchChannel: SearchChannel
}

export const CreateNewSiteModalView = ({ searchChannel }: CreateNewSiteModalViewProps) => {
  const { values, isSubmitting } = useFormikContext<FormValues>()
  useFocusFirstErrorOnSubmit()

  const renderChannel = (
    platformValue: NonNullable<FormValues['platform']>,
    isSubmitting: boolean,
  ) => {
    if (isPlatformSearchable(platformValue as PlatformValue) && !searchChannel.manualURLType) {
      return (
        <Field
          component={ChannelAutoComplete}
          name="searchedChannel"
          platformValue={platformValue}
          autoCompletePlacement="top"
          disabled={isSubmitting}
          {...searchChannel}
        />
      )
    }

    return (
      <Field
        component={ChannelInput}
        name="channelUrl"
        platformValue={platformValue}
        disabled={isSubmitting}
      />
    )
  }

  return (
    <Form>
      <CreateSiteModal isSubmitting={isSubmitting}>
        <Fieldset>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field component={SiteNameInput} name="siteName" disabled={isSubmitting} />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                component={Checkbox}
                name="siteForSomeoneElse"
                label="This site is for someone else"
                disabled={isSubmitting}
              />
            </Fieldset.Item>
          </Fieldset.Row>

          {values.siteForSomeoneElse && (
            <>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Field component={CreatorNameInput} name="creatorName" disabled={isSubmitting} />
                </Fieldset.Item>
              </Fieldset.Row>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Field
                    component={LocationSelect}
                    name="location"
                    label="Creator's location"
                    disabled={isSubmitting}
                  />
                </Fieldset.Item>
              </Fieldset.Row>

              <Fieldset.Row>
                <Fieldset.Item>
                  <S.FieldLabel>Creator's primary platform</S.FieldLabel>
                  <Field component={PlatformSelect} name="platform" disabled={isSubmitting} />
                </Fieldset.Item>
              </Fieldset.Row>

              {values.platform && (
                <Fieldset.Row>
                  <Fieldset.Item>{renderChannel(values.platform, isSubmitting)}</Fieldset.Item>
                </Fieldset.Row>
              )}
            </>
          )}
        </Fieldset>
      </CreateSiteModal>
    </Form>
  )
}
